/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — auth-firm-audit.ts
   Firm audit log viewer (Phase 2 RBAC).
   Reads firmAuditLog entries via the getFirmAuditLog Cloud Function.
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { FIREBASE_ENABLED } from './config';
import { esc, el, showToast } from './utils';

declare const firebase: any;

export interface AuditLogEntry {
  id: string;
  action: 'add' | 'remove' | 'changeRole';
  actorEmail: string;
  actorRole: 'owner' | 'admin' | 'member';
  targetEmail: string;
  newRole?: string | null;
  name?: string | null;
  timestamp: string;
}

function formatActionLabel(entry: AuditLogEntry): string {
  switch (entry.action) {
    case 'add':
      return 'Added ' + esc(entry.targetEmail) + (entry.name ? ' (' + esc(entry.name) + ')' : '');
    case 'remove':
      return 'Removed ' + esc(entry.targetEmail);
    case 'changeRole':
      return 'Changed ' + esc(entry.targetEmail) + ' to ' + esc(entry.newRole || 'member');
    default:
      return esc(entry.action);
  }
}

function actionColor(action: string): string {
  if (action === 'add') return '#16a34a';
  if (action === 'remove') return '#dc2626';
  if (action === 'changeRole') return '#2563eb';
  return '#6b7280';
}

/**
 * Loads and renders the audit log into the #firm-audit-log container.
 * Only owners and admins can view (server-enforced).
 */
export async function loadAuditLog(): Promise<void> {
  const container = el('firm-audit-log');
  if (!container) return;

  if (!FIREBASE_ENABLED || typeof firebase === 'undefined') {
    container.innerHTML = '<p style="color:var(--muted);font-size:0.85rem">Audit log requires Firebase to be enabled.</p>';
    return;
  }

  if (!state.firebaseAuth?.currentUser) {
    container.innerHTML = '<p style="color:var(--muted);font-size:0.85rem">Sign in to view the audit log.</p>';
    return;
  }

  container.innerHTML = '<p style="color:var(--muted);font-size:0.85rem">Loading audit log…</p>';

  try {
    const callable = firebase.functions().httpsCallable('getFirmAuditLog');
    const result = await callable({ limit: 50 });
    const entries: AuditLogEntry[] = (result?.data?.entries || []) as AuditLogEntry[];

    if (entries.length === 0) {
      container.innerHTML = '<p style="color:var(--muted);font-size:0.85rem">No audit entries yet.</p>';
      return;
    }

    let html = '<table style="width:100%;font-size:0.82rem;border-collapse:collapse">';
    html += '<tr style="border-bottom:1px solid var(--border);color:var(--muted);font-size:0.72rem">' +
      '<td style="padding:6px 0">When</td>' +
      '<td>Actor</td>' +
      '<td>Action</td>' +
      '</tr>';
    entries.forEach(function(entry) {
      const date = new Date(entry.timestamp);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      const color = actionColor(entry.action);

      html += '<tr style="border-bottom:1px solid var(--border)">';
      html += '<td style="padding:8px 0;color:var(--muted)">' + esc(dateStr) + ' ' + esc(timeStr) + '</td>';
      html += '<td>' + esc(entry.actorEmail) +
        ' <span style="color:var(--muted);font-size:0.7rem">(' + esc(entry.actorRole) + ')</span></td>';
      html += '<td><span style="color:' + color + ';font-weight:500">' + formatActionLabel(entry) + '</span></td>';
      html += '</tr>';
    });
    html += '</table>';

    container.innerHTML = html;
  } catch (err: any) {
    const msg = err?.message || 'Failed to load audit log.';
    container.innerHTML = '<p style="color:var(--danger);font-size:0.85rem">' + esc(msg) + '</p>';
    showToast('Audit log failed: ' + msg, 'error');
  }
}

/** Toggle the audit log section open/closed and lazy-load entries. */
export function toggleAuditLog(): void {
  const section = el('firm-audit-log-section');
  if (!section) return;
  const isHidden = section.style.display === 'none' || !section.style.display;
  section.style.display = isHidden ? 'block' : 'none';
  if (isHidden) loadAuditLog();
}
